import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api, ApiError } from "../api";
import type { MemberBetRecords, PublicBetRecord } from "../bet-types";
import { Button, SelectField, Spinner, StatusBadge } from "../components/ui";
import { readMobileLocation } from "./navigation";
import { betCategories } from "./game-statistics";
import { RecordEmpty } from "./shared";
import type { MobilePageProps } from "./types";
import "./bet-record-list.css";

const date = new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", dateStyle: "short", timeStyle: "short" });
const names = { 1: "已结算", 2: "未结算", 3: "无效", 4: "已退款" };
const gameTypes = betCategories.map(category => ({ value: String(category.type), label: category.name }));
const pageSize = 30;
type QueryResult = { key: string; data: MemberBetRecords | null; error: string; busy: boolean };

function BetRecordItem({ record }: { record: PublicBetRecord }) {
  const category = gameTypes.find(item => item.value === String(record.gameType))?.label ?? "未知分类";
  return <li className="mobile-record-item member-bet-item">
    <div className="mobile-record-item__heading"><h3>{record.playName || "暂无游戏名称"}</h3><StatusBadge tone={record.status === 1 ? "success" : "neutral"}>{names[record.status]}</StatusBadge></div>
    <p className="member-bet-item__venue">{record.provider} · {category}</p>
    <p>有效投注：{record.status === 1 ? record.validBetAmount : "—"} · 下注金额：{record.betAmount}</p>
    <p>结算盈亏：{record.status === 1 ? record.netAmount : "—"}</p>
    <time dateTime={new Date(record.betTime * 1000).toISOString()}>{date.format(record.betTime * 1000)}</time>
    <details className="member-bet-item__details">
      <summary><span>订单号：{record.billNo}</span><ChevronDown aria-hidden="true"/><span className="sr-only">查看订单详情</span></summary>
      <dl>
        <div><dt>游戏名称</dt><dd>{record.playName || "暂无游戏名称"}</dd></div>
        <div><dt>上游记录号</dt><dd>{record.rowid}</dd></div>
        <div><dt>游戏场馆</dt><dd>{record.provider} · {category}</dd></div>
        <div><dt>下注时间</dt><dd>{date.format(record.betTime * 1000)}（北京时间）</dd></div>
        <div><dt>玩法</dt><dd>{record.playType || "暂无玩法信息"}</dd></div>
        <div><dt>下注金额</dt><dd>{record.betAmount}</dd></div>
        <div><dt>有效投注</dt><dd>{record.status === 1 ? record.validBetAmount : "—"}</dd></div>
        <div><dt>结算盈亏</dt><dd>{record.status === 1 ? record.netAmount : "—"}</dd></div>
        <div><dt>结算状态</dt><dd>{names[record.status]}</dd></div>
        <div><dt>金额单位</dt><dd>场馆原始单位，币种未确认</dd></div>
      </dl>
    </details>
  </li>;
}

export function BetRecordList({ userId, start, end, refreshToken, onNavigate }: {
  userId: string; start: number; end: number; refreshToken: number; onNavigate: MobilePageProps["onNavigate"];
}) {
  const location = readMobileLocation();
  const gameType = /^[1-7]$/.test(location.params.get("betType") ?? "") ? location.params.get("betType")! : "";
  const status = /^[1-4]$/.test(location.params.get("betStatus") ?? "") ? location.params.get("betStatus")! : "";
  const rawPage = location.params.get("betPage") ?? "1";
  const page = /^[1-9]\d{0,6}$/.test(rawPage) && Number(rawPage) <= 1_000_000 ? Number(rawPage) : 1;
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<QueryResult | null>(null);
  const requestKey = JSON.stringify([userId, start, end, gameType, status, page]);
  const current = result?.key === requestKey ? result : null;
  const data = current?.data ?? null;
  const error = current?.error ?? "";
  const busy = current?.busy ?? true;

  function changeQuery(changes: Record<string, string>) {
    const { params } = readMobileLocation();
    params.set("tab", "games");
    for (const [key, value] of Object.entries(changes)) {
      if (value) params.set(key, value); else params.delete(key);
    }
    onNavigate("game-records", Object.fromEntries(params));
  }

  useEffect(() => {
    const controller = new AbortController();
    setResult(previous => ({ key: requestKey, data: previous?.key === requestKey ? previous.data : null, error: "", busy: true }));
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), from: String(Math.floor(start / 1000)), to: String(Math.floor((end - 1) / 1000)) });
    if (gameType) params.set("gameType", gameType);
    if (status) params.set("status", status);
    void api<MemberBetRecords>(`/api/user/bet-records?${params}`, { signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]), cache: "no-store" })
      .then(payload => { if (!controller.signal.aborted) setResult({ key: requestKey, data: payload, error: "", busy: false }); })
      .catch(reason => {
        if (!controller.signal.aborted) setResult(previous => ({ key: requestKey, data: previous?.key === requestKey ? previous.data : null, error: reason instanceof ApiError ? reason.message : "游戏注单读取失败，请重试。", busy: false }));
      });
    return () => controller.abort();
  }, [requestKey, start, end, gameType, status, page, refreshToken, retry]);

  const pages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const settledCount = data?.totalsByProvider.reduce((sum, item) => sum + item.settledCount, 0) ?? 0;
  const filtered = Boolean(gameType || status);
  return <div className="member-bet-records" aria-busy={busy}>
    <div className="member-bet-filters">
      <SelectField label="游戏分类" value={gameType} onChange={event => changeQuery({ betType: event.target.value, betPage: "" })}>
        <option value="">全部分类</option>{gameTypes.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
      </SelectField>
      <SelectField label="结算状态" value={status} onChange={event => changeQuery({ betStatus: event.target.value, betPage: "" })}>
        <option value="">全部状态</option>{Object.entries(names).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
      </SelectField>
    </div>
    {filtered && <div className="member-bet-filter-reset"><Button emphasis="ghost" onClick={() => changeQuery({ betType: "", betStatus: "", betPage: "" })}>清除筛选</Button></div>}
    {error && <div className="mobile-record-refresh-error" role="alert"><span>{error}{data ? " 当前保留上次读取的记录。" : ""}</span><Button disabled={busy} onClick={() => setRetry(value => value + 1)}>重试</Button></div>}
    {!data ? busy ? <div className="member-bet-loading"><Spinner label="正在读取游戏注单"/></div> : null : <>
      <div className="member-bet-counts" role="status"><span>已采集 <strong>{data.lastSyncedAt ? data.total : "—"}</strong> 笔</span><span>已结算 <strong>{data.lastSyncedAt ? settledCount : "—"}</strong> 笔</span>{busy && <span className="sr-only">正在刷新游戏注单</span>}</div>
      <p className="member-bet-source">{data.lastSyncedAt ? `更新于 ${date.format(new Date(data.lastSyncedAt))}（北京时间）` : "游戏记录尚未同步"}。按下注时间查询，记录随已完成的采集批次更新。</p>
      {data.totalsByProvider.length > 0 && <section className="member-bet-totals" aria-label="按场馆统计">
        <h3>已结算统计</h3><p>金额为各场馆原始单位，币种未确认，不跨场馆合计。</p>
        {data.totalsByProvider.map(item => <div className="member-bet-total" key={item.provider}><strong>{item.provider}<small>{item.settledCount} 笔已结算</small></strong><dl><div><dt>有效投注</dt><dd>{item.validBetAmount}</dd></div><div><dt>场馆盈亏</dt><dd>{item.netAmount}</dd></div></dl></div>)}
      </section>}
      {data.total > 0 && data.totalsByProvider.length === 0 && <p className="member-bet-source">当前筛选中暂无已结算注单。</p>}
      {data.records.length ? <ul className="mobile-record-list" aria-label="游戏注单">{data.records.map(record => <BetRecordItem record={record} key={record.key}/>)}</ul> : <div className="mobile-record-empty-slot">
        <RecordEmpty message={page > pages ? "当前页暂无注单" : filtered ? "没有符合筛选的注单" : "暂无游戏注单"} detail={data.lastSyncedAt ? filtered ? "可调整游戏分类、结算状态或查询日期。" : "已采集数据中没有当前日期范围的游戏记录。" : "完成场馆数据同步后，你的游戏记录会显示在这里。"} action={page > pages ? <Button emphasis="outline" onClick={() => changeQuery({ betPage: "" })}>返回第一页</Button> : undefined}/>
      </div>}
      {data.total > pageSize && page <= pages && <div className="mobile-record-pagination member-bet-pagination"><span>第 {page} / {pages} 页 · 共 {data.total} 笔</span><div><Button emphasis="outline" disabled={busy || page <= 1} onClick={() => changeQuery({ betPage: String(page - 1) })}>上一页</Button><Button emphasis="outline" disabled={busy || page >= pages} onClick={() => changeQuery({ betPage: String(page + 1) })}>下一页</Button></div></div>}
    </>}
  </div>;
}
